import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { LpSectionKey } from '@/services/lp/types';
import type { DaGatewayLastCompletedPage } from '@/services/lp/contracts';

export interface LpApplicationState {
  applicationId: string;
  loanId: string;
  token: string;
  lastCompletedPage: DaGatewayLastCompletedPage | null;
  sections: Partial<Record<LpSectionKey, unknown>>;
  updatedAtBySection: Partial<Record<LpSectionKey, string>>;
}

const initialState: LpApplicationState = {
  applicationId: '',
  loanId: '',
  token: '',
  lastCompletedPage: null,
  sections: {},
  updatedAtBySection: {},
};

const LpApplication = createSlice({
  name: 'LpApplication',
  initialState,
  reducers: {
    setSectionConfirmed: (
      state,
      action: PayloadAction<{
        applicationId?: string;
        loanId?: string;
        section: LpSectionKey;
        value: unknown;
      }>,
    ) => {
      const applicationId = action.payload.applicationId || action.payload.loanId || '';
      const loanId = action.payload.loanId || '';

      if (state.applicationId && applicationId && state.applicationId !== applicationId) {
        state.sections = {};
        state.updatedAtBySection = {};
        state.lastCompletedPage = null;
      }

      state.applicationId = applicationId || state.applicationId;
      state.loanId = loanId || applicationId || state.loanId;
      state.sections[action.payload.section] = action.payload.value;
      state.updatedAtBySection[action.payload.section] = new Date().toISOString();
    },
    setSession: (
      state,
      action: PayloadAction<{
        applicationId?: string;
        loanId?: string;
        token?: string;
        lastCompletedPage?: DaGatewayLastCompletedPage | null;
      }>,
    ) => {
      const applicationId = action.payload.applicationId || action.payload.loanId || '';

      if (state.applicationId && applicationId && state.applicationId !== applicationId) {
        state.sections = {};
        state.updatedAtBySection = {};
        state.lastCompletedPage = null;
      }

      state.applicationId = applicationId || state.applicationId;
      state.loanId = action.payload.loanId || applicationId || state.loanId;
      state.token = action.payload.token || state.token;
      if (action.payload.lastCompletedPage !== undefined) {
        state.lastCompletedPage = action.payload.lastCompletedPage;
      }
    },
    hydrateConfirmedSections: (
      state,
      action: PayloadAction<{
        applicationId?: string;
        loanId?: string;
        token?: string;
        lastCompletedPage?: DaGatewayLastCompletedPage | null;
        sections: Partial<Record<LpSectionKey, unknown>>;
      }>,
    ) => {
      const applicationId = action.payload.applicationId || action.payload.loanId || '';
      if (state.applicationId && applicationId && state.applicationId !== applicationId) {
        state.lastCompletedPage = null;
      }
      state.applicationId = applicationId;
      state.loanId = action.payload.loanId || applicationId;
      state.token = action.payload.token || state.token;
      if (action.payload.lastCompletedPage !== undefined) {
        state.lastCompletedPage = action.payload.lastCompletedPage;
      }
      state.sections = action.payload.sections;
      state.updatedAtBySection = Object.keys(action.payload.sections).reduce<Partial<Record<LpSectionKey, string>>>((acc, section) => {
        acc[section as LpSectionKey] = new Date().toISOString();
        return acc;
      }, {});
    },
    resetState: () => initialState,
  },
});

export const { setSectionConfirmed, setSession, hydrateConfirmedSections } = LpApplication.actions;

export const selectLpConfirmedSection = (state: RootState, section: LpSectionKey): unknown => {
  return state.LpApplication.sections[section];
};

export default LpApplication.reducer;
